import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CachePage from './CachePage';
import CronServerPage from './CronServerPage';
import DashboardLayoutPage from './DashboardLayoutPage';
import GraphVersionManagementPage from './GraphVersionManagementPage';
import QueueStatusPage from './QueueStatusPage';
import DatabaseManagementPage from './DatabaseManagementPage';
import { getUserRole } from '../utils/tokenUtils';

type SettingsSection = 'dashboard-layout' | 'cache-management' | 'cron-management' | 'cron-execution' | 'api-version-management' | 'queue-status' | 'database-management';

const SettingsPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<SettingsSection | null>(null);
  const navigate = useNavigate();
  const isSuperAdmin = getUserRole() === 'ROLE_SUPER_ADMIN';

  // Check if user has access to a specific section (super admin has access to all sections)
  const hasAccessToSection = (_sectionId: SettingsSection): boolean => {
    return isSuperAdmin;
  };

  const handleBackToDashboards = () => {
    navigate('/');
  };

  const settingsOptions = [
    {
      id: 'dashboard-layout' as SettingsSection,
      label: 'Dashboard Layout',
      description: 'Customize dashboard appearance and layout',
      available: hasAccessToSection('dashboard-layout'),
      comingSoon: false, // Page exists
      hasAccess: hasAccessToSection('dashboard-layout'),
      isNew: true // New feature
    },
    {
      id: 'api-version-management' as SettingsSection,
      label: 'Graph Version Management',
      description: 'Select and update graph version to switch API for a graph',
      available: hasAccessToSection('api-version-management'),
      comingSoon: false, // Page exists
      hasAccess: hasAccessToSection('api-version-management'),
      isNew: true // New feature
    },
    {
      id: 'queue-status' as SettingsSection,
      label: 'Queue Status',
      description: 'View background jobs from backend queue',
      available: hasAccessToSection('queue-status'),
      comingSoon: false,
      hasAccess: hasAccessToSection('queue-status'),
      isNew: true
    },
    {
      id: 'database-management' as SettingsSection,
      label: 'Database Management',
      description: 'Monitor pool load, stuck queries, and kill runaway SQL',
      available: hasAccessToSection('database-management'),
      comingSoon: false,
      hasAccess: hasAccessToSection('database-management'),
      isNew: true
    },
    {
      id: 'cache-management' as SettingsSection,
      label: 'Cache Management',
      description: 'Manage application cache and data synchronization',
      available: hasAccessToSection('cache-management'),
      comingSoon: false, // Page exists
      hasAccess: hasAccessToSection('cache-management')
    },
    {
      id: 'cron-management' as SettingsSection,
      label: 'Cron Management',
      description: 'Monitor and manage scheduled tasks',
      available: hasAccessToSection('cron-management'),
      comingSoon: false, // Page exists
      hasAccess: hasAccessToSection('cron-management')
    },
    {
      id: 'cron-execution' as SettingsSection,
      label: 'Cron Execution Control',
      description: 'Control the execution of cron jobs',
      available: hasAccessToSection('cron-execution'),
      comingSoon: true, // Page not created yet
      hasAccess: hasAccessToSection('cron-execution')
    }
  ];

  const renderContent = () => {
    if (!activeSection) {
      return (
        <div className="settings-welcome">
          <div className="welcome-content">
            <div className="welcome-icon">⚙️</div>
            <h2>Settings</h2>
            <p>Please select an option from the sidebar to configure your settings.</p>
            <div className="available-options">
              <h3>Available Options:</h3>
              <ul>
                {settingsOptions.map((option) => {
                  // Determine the status based on access and page availability
                  let status = '';
                  let statusClass = '';

                  if (!option.hasAccess) {
                    status = '❌ Access Denied';
                    statusClass = 'denied';
                  } else if (option.comingSoon) {
                    status = '🚧 Coming Soon';
                    statusClass = 'coming-soon';
                  } else {
                    status = '✓ Available';
                    statusClass = 'available';
                  }

                  return (
                    <li key={option.id} className={option.hasAccess ? 'available' : 'unavailable'}>
                      <strong>{option.label}</strong>
                      <span className={`status-badge ${statusClass}`}>{status}</span>
                      <p>{option.description}</p>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      );
    }

    switch (activeSection) {
      case 'dashboard-layout':
        return <DashboardLayoutPage />;
      case 'cache-management':
        return <CachePage />;
      case 'cron-management':
        return <CronServerPage />;
      case 'api-version-management':
        return <GraphVersionManagementPage />;
      case 'queue-status':
        return <QueueStatusPage />;
      case 'database-management':
        return <DatabaseManagementPage />;
      case 'cron-execution':
      default:
        return (
          <div className="settings-placeholder">
            <div className="placeholder-content">
              <h3>Coming Soon</h3>
              <p>This feature is under development and will be available soon.</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="settings-page">
      <div className="layout-header">
        <div className="header-content">
          <h2>Settings</h2>
          <p>Configure application settings and manage system components</p>
        </div>
        <button 
          className="btn btn-secondary"
          onClick={handleBackToDashboards}
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Dashboards
        </button>
      </div>

      <div className="settings-container">
        <div className="settings-sidebar">
          <div className="settings-nav">
            {settingsOptions.map((option) => (
              <div
                key={option.id}
                className={`settings-nav-item ${activeSection === option.id ? 'active' : ''} ${!option.available ? 'disabled' : ''}`}
                onClick={() => option.available && setActiveSection(option.id)}
              >
                <div className="nav-item-content">
                  <h3>{option.label}</h3>
                  <p>{option.description}</p>
                  {!option.hasAccess && (
                    <span className="access-denied-badge">Access Denied</span>
                  )}
                  {option.hasAccess && option.comingSoon && (
                    <span className="coming-soon-badge">Coming Soon</span>
                  )}
                  {option.isNew && (
                    <span className="new-badge">NEW</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="settings-content">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
